import React from "react";
import {
  Home,
  Umbrella,
  CloudRain,
  Car,
  Sun,
  Truck,
  Waves,
  Ship,
  Building2,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/data/content";

const ICON_MAP = { Home, Umbrella, CloudRain, Car, Sun, Truck, Waves, Ship, Building2 };

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 bg-black overflow-hidden"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C4FF00] mb-4">
              — Services
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              Every surface.
              <br />
              <span className="text-zinc-500">One trusted crew.</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
              We combine high-pressure and low-pressure soft-wash techniques so each
              surface gets the right treatment — no damaged siding, no bleached lawns,
              no shortcuts.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative max-w-sm mx-auto lg:ml-auto lg:mr-0">
              <div className="absolute -inset-4 rounded-[1.75rem] bg-gradient-to-br from-[#00BFFF]/20 via-transparent to-[#C4FF00]/20 blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-zinc-800 aspect-[4/3]">
                <img
                  src="https://customer-assets.emergentagent.com/job_spotless-water-works/artifacts/ohkyffb2_van.jpeg"
                  alt="Spotless Water Works branded service van"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="services-van"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-white px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-zinc-800">
                    Our Fleet
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-widest text-[#C4FF00] px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-zinc-800">
                    On the Road
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {SERVICES.map((s, idx) => {
            const Icon = ICON_MAP[s.icon] || Home;
            const colSpan = s.featured
              ? "md:col-span-3"
              : "md:col-span-2";
            const isAccent = idx === 0 || idx === 3;
            return (
              <a
                key={s.id}
                href="#contact"
                className={`group relative overflow-hidden rounded-2xl border border-zinc-800 bg-[#0A0A0A] p-7 md:p-9 hover:border-[#C4FF00]/50 transition-all duration-300 hover:-translate-y-1 ${colSpan}`}
                data-testid={`service-card-${s.id}`}
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#00BFFF]/5 blur-3xl group-hover:bg-[#C4FF00]/10 transition-colors duration-500" />
                <div className="relative flex flex-col h-full min-h-[220px]">
                  <div
                    className={`h-14 w-14 rounded-xl grid place-items-center border ${
                      isAccent
                        ? "bg-[#C4FF00] border-[#C4FF00] text-black"
                        : "bg-black border-zinc-800 text-[#00BFFF] group-hover:border-[#C4FF00] group-hover:text-[#C4FF00]"
                    } transition-colors`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="mt-6 font-display text-2xl md:text-3xl uppercase tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">
                    {s.blurb}
                  </p>

                  <div className="mt-auto pt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500 group-hover:text-[#C4FF00] transition-colors">
                    Book this service
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
